import { LitElement, html } from 'lit';

import { api } from '../api/index.js';
import { poll } from '../poll.js';
import { formStyles } from '../../styles/shared/forms.js';
import { pageStyles } from '../../styles/shared/page.js';
import { enrollmentTokenStyles } from '../../styles/components/enrollment-token.js';
import { confirmAction } from './plane-confirm.js';
import { when } from './time.js';
import { customerMembersStyles } from '../../styles/components/customer-members.js';

const ROLE_LABELS = { owner: 'Owner', member: 'Member' };

/**
 * The members of a customer and, for those who manage it (its owners and operators), their roles,
 * the invitation links and the pending invitations. Owners invite, change roles and remove members;
 * members only see who belongs to the customer. A new link is shown once: the plane keeps only its
 * hash.
 */
class CustomerMembers extends LitElement {
  static properties = {
    customerId: { type: String },
    customerName: { type: String },
    /** Whether the viewer manages the customer: an owner of it, or an operator. */
    canManage: { type: Boolean },
    busy: { state: true },
    actionError: { state: true },
    issued: { state: true },
  };

  static styles = [
    pageStyles,
    formStyles,
    enrollmentTokenStyles,
    customerMembersStyles,
  ];

  constructor() {
    super();
    this.customerId = '';
    this.customerName = '';
    this.canManage = false;
    this.busy = false;
    this.actionError = null;
    this.issued = null;
    this.poll = poll(this, async () => {
      const [members, invitations] = await Promise.all([
        api.customerMembers(this.customerId),
        this.canManage ? api.customerInvitations(this.customerId) : Promise.resolve([]),
      ]);
      return { members, invitations };
    });
  }

  async act(action) {
    this.busy = true;
    this.actionError = null;
    try {
      return await action();
    } catch (error) {
      this.actionError = error.message;
      return null;
    } finally {
      this.busy = false;
      this.poll.refresh();
      this.dispatchEvent(new CustomEvent('members-changed', { bubbles: true, composed: true }));
    }
  }

  async invite(event) {
    event.preventDefault();
    const form = event.target;
    const email = form.elements.email.value.trim();
    const issued = await this.act(() =>
      api.inviteToCustomer(this.customerId, { role: form.elements.role.value, email: email || null }),
    );
    if (issued) {
      this.issued = issued;
      form.reset();
    }
  }

  changeRole(member) {
    const role = member.role === 'owner' ? 'member' : 'owner';
    this.act(() => api.changeCustomerUserRole(this.customerId, member.username, role));
  }

  async removeMember(member) {
    if (
      !(await confirmAction({
        heading: `Remove ${member.username} from ${this.customerName}?`,
        message: 'They will no longer see its instances.',
        confirmLabel: 'Remove',
        tone: 'danger',
      }))
    ) {
      return;
    }
    this.act(() => api.removeCustomerUser(this.customerId, member.username));
  }

  revoke(invitation) {
    this.act(() => api.revokeCustomerInvitation(this.customerId, invitation.id));
  }

  async copy() {
    try {
      await navigator.clipboard.writeText(this.issued.url);
    } catch {
      // no clipboard (an insecure origin, a denied permission): the link stays selectable
    }
  }

  renderIssued() {
    const issued = this.issued;
    if (!issued.url) {
      // with invitation mail configured, a link for an email address goes by mail only
      return html`
        <div class="notice" data-tone="success" role="status">
          <strong>Invitation sent by email (${ROLE_LABELS[issued.role]})</strong>
          <p>The link went to the address given, and only there. It works once and expires ${when(issued.expiresAt)}.</p>
          <p class="actions"><button class="small" @click=${() => (this.issued = null)}>OK</button></p>
        </div>
      `;
    }
    return html`
      <div class="notice" data-tone="warning" role="status">
        <strong>Invitation link (${ROLE_LABELS[issued.role]})</strong>
        <p>
          Copy it now: it is shown only once. It works once and expires ${when(issued.expiresAt)}.
          Send it to the person you invite.
        </p>
        <code class="token">${issued.url}</code>
        <p class="actions">
          <button class="small" @click=${() => this.copy()}>Copy link</button>
          <button class="small" @click=${() => (this.issued = null)}>I have sent it</button>
        </p>
      </div>
    `;
  }

  renderMember(member) {
    return html`
      <li>
        <span>${member.username} <span class="role">${ROLE_LABELS[member.role]}</span></span>
        ${this.canManage
          ? html`<span class="actions">
              <button class="small" ?disabled=${this.busy} @click=${() => this.changeRole(member)}>
                ${member.role === 'owner' ? 'Make member' : 'Make owner'}
              </button>
              <button class="small danger" ?disabled=${this.busy} @click=${() => this.removeMember(member)}>Remove</button>
            </span>`
          : null}
      </li>
    `;
  }

  renderInvitations(invitations) {
    if (invitations.length === 0) {
      return null;
    }
    return html`
      <h3>Pending invitations</h3>
      <ul>
        ${invitations.map(
          (invitation) => html`
            <li>
              <span>
                ${ROLE_LABELS[invitation.role]}${invitation.invitedBy ? `, by ${invitation.invitedBy}` : ''}
                <span class="role">expires ${when(invitation.expiresAt)}</span>
              </span>
              <button class="small danger" ?disabled=${this.busy} @click=${() => this.revoke(invitation)}>Revoke</button>
            </li>
          `,
        )}
      </ul>
    `;
  }

  render() {
    const value = this.poll.value;
    if (value === undefined) {
      return this.poll.error ? html`<p class="muted">The members could not be loaded: ${this.poll.error.message}</p>` : null;
    }
    const { members, invitations } = value;
    return html`
      ${this.actionError ? html`<div class="notice" data-tone="danger" role="alert"><strong>The action failed</strong>${this.actionError}</div>` : null}
      <h3>Members</h3>
      ${members.length
        ? html`<ul>
            ${members.map((member) => this.renderMember(member))}
          </ul>`
        : html`<p class="muted">Nobody sees these workspaces yet: invite its first owner.</p>`}
      ${this.canManage
        ? html`
            ${this.renderInvitations(invitations)} ${this.issued ? this.renderIssued() : null}
            <form class="inline" @submit=${(event) => this.invite(event)}>
              <select name="role" aria-label="Role of the person invited to ${this.customerName}">
                <option value="member" ?selected=${members.some((member) => member.role === 'owner')}>Member</option>
                <option value="owner" ?selected=${!members.some((member) => member.role === 'owner')}>Owner</option>
              </select>
              <input name="email" type="email" placeholder="Email (optional)" aria-label="Email of the person invited to ${this.customerName}" />
              <button class="small" ?disabled=${this.busy}>Create invitation link</button>
            </form>
            <p class="muted">
              An <strong>owner</strong> changes the workspaces and invites people; a <strong>member</strong> sees them and can restart them, nothing more. The email is optional:
              without it you get a link to send yourself, by message or email. A link works once, for one person, for 7 days.
            </p>
          `
        : null}
    `;
  }
}

customElements.define('plane-customer-members', CustomerMembers);
